/**
 * 統一錯誤解析工具
 * 符合 SSOT 原則，用於解析後端返回的標準錯誤格式（ErrorResponseSchema）
 */

/** 驗證錯誤項目（API 可能回傳的結構） */
interface ValidationErrorItem {
  field?: string;
  path?: string[];
  message?: string;
}

/** 錯誤回應資料結構（來自 API 的未知形狀） */
interface ErrorDataShape {
  code?: string;
  message?: string;
  details?:
    | {
        validationErrors?: ValidationErrorItem[];
        message?: string;
      }
    | ValidationErrorItem[];
  error?: {
    issues?: Array<{ path?: string[]; message?: string }>;
    message?: string;
  };
  issues?: Array<{ path?: string[]; message?: string; field?: string }>;
}

/**
 * 預設欄位名稱映射（API 欄位名稱 → 中文名稱）
 */
const DEFAULT_FIELD_NAME_MAP: Record<string, string> = {
  phone: '電話',
  birthday: '生日',
  pin_code: 'PIN 碼',
  name: '姓名',
  status: '狀態',
  email: '電子郵件',
  password: '密碼',
  tenant_name: '商家名稱',
  slug: '識別碼',
};

/**
 * 解析錯誤回應，返回用戶友好的錯誤訊息
 *
 * @param errorData - 錯誤回應資料（來自 API）
 * @param options - 選項
 * @param options.fieldNameMap - 欄位名稱映射（可選，會與預設映射合併）
 * @param options.defaultMessage - 預設錯誤訊息（當無法解析時使用）
 * @returns 用戶友好的錯誤訊息字串
 *
 * @example
 * ```typescript
 * const errorMessage = parseErrorResponse(errorResponse.data, {
 *   fieldNameMap: { custom_field: '自訂欄位' },
 *   defaultMessage: '操作失敗'
 * });
 * ```
 */
export function parseErrorResponse(
  errorData: unknown,
  options?: {
    fieldNameMap?: Record<string, string>;
    defaultMessage?: string;
  }
): string {
  const { fieldNameMap = {}, defaultMessage = '操作失敗' } = options || {};

  // 合併欄位名稱映射（自訂映射優先於預設映射）
  const mergedFieldNameMap = {
    ...DEFAULT_FIELD_NAME_MAP,
    ...fieldNameMap,
  };

  // 如果 errorData 不是物件，返回預設訊息
  if (!errorData || typeof errorData !== 'object') {
    return defaultMessage;
  }

  const data = errorData as ErrorDataShape;

  // 調試：記錄輸入資料結構（僅在開發環境）
  if (typeof window !== 'undefined' && import.meta.env?.DEV) {
    console.log(
      '[parseErrorResponse] 輸入資料:',
      JSON.stringify(data, null, 2)
    );
  }

  // 優先檢查標準格式（新格式）：details.validationErrors
  if (
    data.details &&
    typeof data.details === 'object' &&
    !Array.isArray(data.details) &&
    'validationErrors' in data.details &&
    Array.isArray(data.details.validationErrors)
  ) {
    const validationErrors = data.details.validationErrors;
    const fieldMessages = validationErrors.map((err: ValidationErrorItem) => {
      const field = err.field || err.path?.join('.') || '';
      const fieldName = mergedFieldNameMap[field] || field || '未知欄位';
      const message = err.message || '驗證失敗';
      return `${fieldName}：${message}`;
    });

    if (fieldMessages.length > 0) {
      return fieldMessages.join('；');
    }

    // 如果沒有具體的欄位錯誤，返回通用訊息
    return data.message || '請求參數驗證失敗';
  }

  // 檢查舊格式：details 直接是陣列
  if (Array.isArray(data.details)) {
    const validationErrors = data.details as ValidationErrorItem[];
    const fieldMessages = validationErrors.map((err: ValidationErrorItem) => {
      const field = err.field || err.path?.join('.') || '';
      const fieldName = mergedFieldNameMap[field] || field || '未知欄位';
      const message = err.message || '驗證失敗';
      return `${fieldName}：${message}`;
    });

    if (fieldMessages.length > 0) {
      return fieldMessages.join('；');
    }
  }

  // 檢查 ZodError 格式：error.issues（包裝在 error 物件中）
  if (data.error?.issues && Array.isArray(data.error.issues)) {
    const validationErrors = data.error.issues.map(
      (issue: { path?: string[]; message?: string }) => ({
        field: issue.path?.join('.') || '',
        message: issue.message || '驗證失敗',
        path: issue.path || [],
      })
    );

    const fieldMessages = validationErrors.map(
      (err: { field?: string; message?: string }) => {
        const fieldName =
          mergedFieldNameMap[err.field ?? ''] || err.field || '未知欄位';
        return `${fieldName}：${err.message}`;
      }
    );

    if (fieldMessages.length > 0) {
      return fieldMessages.join('；');
    }
  }

  // 檢查 ZodError 格式：直接是 issues 陣列（ZodError 物件本身）
  if (data.issues && Array.isArray(data.issues)) {
    const validationErrors = data.issues.map(
      (issue: { path?: string[]; message?: string; field?: string }) => ({
        field: issue.path?.join('.') || issue.field || '',
        message: issue.message || '驗證失敗',
        path: issue.path || [],
      })
    );

    const fieldMessages = validationErrors.map(
      (err: { field?: string; message?: string }) => {
        const fieldName =
          mergedFieldNameMap[err.field ?? ''] || err.field || '未知欄位';
        return `${fieldName}：${err.message ?? '驗證失敗'}`;
      }
    );

    if (fieldMessages.length > 0) {
      return fieldMessages.join('；');
    }
  }

  // 檢查 ZodError 格式：error.message 是 JSON 字串（包含 issues 陣列）
  if (data.error?.message && typeof data.error.message === 'string') {
    try {
      // 嘗試解析 JSON 字串
      const parsedMessage = JSON.parse(data.error.message);
      if (Array.isArray(parsedMessage)) {
        // 如果解析後是陣列，視為 issues
        const validationErrors = parsedMessage.map(
          (issue: { path?: string[]; message?: string }) => ({
            field: issue.path?.join('.') || '',
            message: issue.message || '驗證失敗',
            path: issue.path || [],
          })
        );

        const fieldMessages = validationErrors.map(
          (err: { field?: string; message?: string }) => {
            const fieldName =
              mergedFieldNameMap[err.field ?? ''] || err.field || '未知欄位';
            return `${fieldName}：${err.message}`;
          }
        );

        if (fieldMessages.length > 0) {
          return fieldMessages.join('；');
        }
      }
    } catch {
      // 如果解析失敗，繼續處理其他格式
    }
  }

  // 檢查 error 欄位（字串格式）
  if (data.error && typeof data.error === 'string') {
    return data.error;
  }

  // 檢查 message 欄位
  if (data.message && typeof data.message === 'string') {
    return data.message;
  }

  // 檢查 details.message 欄位
  if (hasDetailsMessage(data.details)) {
    return data.details.message;
  }

  // 如果都無法解析，返回預設訊息
  return defaultMessage;
}

/** 供 parseFieldErrors 使用的 details.message 型別守衛 */
function hasDetailsMessage(
  details: ErrorDataShape['details']
): details is { message: string } {
  return (
    details != null &&
    typeof details === 'object' &&
    !Array.isArray(details) &&
    'message' in details &&
    typeof (details as { message?: string }).message === 'string'
  );
}

/**
 * 解析錯誤回應，返回欄位級別的錯誤物件
 *
 * @param errorData - 錯誤回應資料（來自 API）
 * @param options - 選項
 * @param options.fieldNameMap - 欄位名稱映射（可選，會與預設映射合併）
 * @returns 欄位錯誤物件，key 為欄位名稱，value 為錯誤訊息
 *
 * @example
 * ```typescript
 * const fieldErrors = parseFieldErrors(errorResponse.data);
 * // { phone: '請輸入有效台灣手機號碼 (格式: 09XXXXXXXX)', name: '請輸入姓名' }
 * ```
 */
export function parseFieldErrors(errorData: unknown): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  // 如果 errorData 不是物件，返回空物件
  if (!errorData || typeof errorData !== 'object') {
    return fieldErrors;
  }

  const data = errorData as ErrorDataShape;

  // 優先檢查標準格式（新格式）：details.validationErrors
  if (
    data.details &&
    typeof data.details === 'object' &&
    !Array.isArray(data.details) &&
    'validationErrors' in data.details &&
    Array.isArray(data.details.validationErrors)
  ) {
    data.details.validationErrors.forEach((err: ValidationErrorItem) => {
      const field = err.field || err.path?.join('.') || '';
      if (field) {
        fieldErrors[field] = err.message || '驗證失敗';
      }
    });
    return fieldErrors;
  }

  // 檢查舊格式：details 直接是陣列
  if (Array.isArray(data.details)) {
    (data.details as ValidationErrorItem[]).forEach(
      (err: ValidationErrorItem) => {
        const field = err.field || err.path?.join('.') || '';
        if (field) {
          fieldErrors[field] = err.message || '驗證失敗';
        }
      }
    );
    return fieldErrors;
  }

  // 檢查 ZodError 格式：error.issues（包裝在 error 物件中）
  if (data.error?.issues && Array.isArray(data.error.issues)) {
    data.error.issues.forEach(
      (issue: { path?: string[]; message?: string }) => {
        const field = issue.path?.join('.') || '';
        if (field) {
          fieldErrors[field] = issue.message || '驗證失敗';
        }
      }
    );
    return fieldErrors;
  }

  // 檢查 ZodError 格式：直接是 issues 陣列（ZodError 物件本身）
  if (data.issues && Array.isArray(data.issues)) {
    data.issues.forEach(
      (issue: { path?: string[]; message?: string; field?: string }) => {
        const field = issue.path?.join('.') || issue.field || '';
        if (field) {
          fieldErrors[field] = issue.message || '驗證失敗';
        }
      }
    );
    return fieldErrors;
  }

  // 檢查 ZodError 格式：error.message 是 JSON 字串（包含 issues 陣列）
  if (data.error?.message && typeof data.error.message === 'string') {
    try {
      const parsedMessage = JSON.parse(data.error.message);
      if (Array.isArray(parsedMessage)) {
        parsedMessage.forEach(
          (issue: { path?: string[]; message?: string }) => {
            const field = issue.path?.join('.') || '';
            if (field) {
              fieldErrors[field] = issue.message || '驗證失敗';
            }
          }
        );
        return fieldErrors;
      }
    } catch {
      // 如果解析失敗，繼續處理其他格式
    }
  }

  return fieldErrors;
}

/**
 * 從 catch 到的 error 取出要顯示在 Toast 的訊息（僅用 message，絕不顯示 code）
 *
 * @param error - catch 到的未知錯誤（可能含 response.data）
 * @param fallback - 無法解析時的回退訊息
 * @returns 使用者可見的錯誤訊息字串
 */
export function extractToastMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } })
      .response;
    const message = response?.data?.message;
    if (typeof message === 'string' && message) {
      return message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}
